<template>
  <div>
    <div class="boy-history__header">
      <h1 class="h2 mb-3">BOY History</h1>
      <app-button :block="false" @click="openAdd">Add New BOY Status</app-button>
    </div>

    <div v-if="type === 'horse'" class="alert alert-info">
      Every BOY History entry for a horse must have an Owner — entries without one are excluded from
      results and standings for any draft on or after their Effective Date.
    </div>

    <app-alert />

    <responsive-table
      :columns="columns"
      :rows="sortedHistory"
      :loading="loading"
      :breakpoints="breakpoints"
      :sort="sortField"
      :direction="sortDir"
      :row-class="rowClass"
      @sort="setSort"
      empty-text="No BOY status history found."
    >
      <template #cell:effective_date="{ row }">{{ formatDate(row.effective_date, 'long') }}</template>
      <template #cell:boy="{ row }">
        <router-link v-if="boyStatusTitleLink(row)" :to="boyStatusTitleLink(row)" target="_blank" title="View this title's standings preview">{{ row.boy_status.name }}</router-link>
        <template v-else>{{ row.boy_status.name }}</template>
      </template>
      <template #cell:status="{ row }">{{ type === 'horse' ? row.horse_status.name : row.rider_status.name }}</template>
      <template v-if="type === 'horse'" #cell:owner="{ row }">
        <template v-if="row.owner">{{ row.owner.name }}<small v-if="row.owner.deleted" class="text-primary">(deleted)</small></template>
        <template v-else>—</template>
      </template>
      <template v-if="type === 'rider'" #cell:zone="{ row }">{{ row.zone ? row.zone.name : '—' }}</template>
      <template #cell:changed_by="{ row }">{{ row.changed_by.name }} on {{ formatDate(row.changed_by.date, 'long') }}</template>
      <template #cell:actions="{ row }">
        <div class="actions__wrap">
          <button class="icon-btn icon-btn--edit" title="Backdate" @click="openEdit(row)" />
          <button class="icon-btn icon-btn--view" title="View Affected Results" @click="viewResults(row)" />
          <button
            v-if="row.needs_recalculation"
            class="icon-btn icon-btn--clear"
            title="Clear recalculation flag"
            :disabled="saving"
            @click="confirmClearFlag(row)"
          />
          <button
            v-if="row.created_manually"
            class="icon-btn icon-btn--delete"
            title="Delete"
            :disabled="saving"
            @click="confirmDelete(row)"
          />
          <span v-if="row.needs_recalculation" class="draws-dashboard__warn-icon" title="Needs recalculation" />
        </div>
      </template>
    </responsive-table>

    <boy-status-form-modal
      v-if="formVisible"
      :type="type"
      :status-options="statusOptions"
      :owner-options="ownerOptions"
      :owner-loading="ownerLoading"
      :saving="saving"
      :error="formError"
      :editing="editingRow"
      @owner-search="onOwnerSearch"
      @submit="handleSubmit"
      @cancel="closeForm"
    />

    <confirm-modal
      v-if="clearingFlagRow"
      message="Are you sure you want to clear the recalculation flag on this entry? It won't be marked as needing a recalculation anymore."
      :loading="saving"
      @confirm="handleClearFlag"
      @cancel="clearingFlagRow = null"
    />

    <confirm-modal
      v-if="deletingRow"
      message="Are you sure you want to delete this BOY status entry? This cannot be undone."
      :loading="saving"
      @confirm="handleDelete"
      @cancel="deletingRow = null"
    />

    <action-gate-modal v-if="gateVisible" :error="gateError" :loading="gateLoading" @confirm="submitGatePassword" @cancel="cancelGate" />
  </div>
</template>

<script>
import {
  defineComponent, ref, computed, onMounted, watch, toRefs, getCurrentInstance
} from '@vue/composition-api'
import useBoyStatusHistory from '@/composables/useBoyStatusHistory'
import useActionGate from '@/composables/useActionGate'
import ActionGateModal from '@/components/ActionGateModal'
import AppButton from '@/components/AppButton'
import AppAlert from '@/components/AppAlert'
import ConfirmModal from '@/components/ConfirmModal'
import ResponsiveTable from '@/components/ResponsiveTable'
import { formatDate } from '@/utils/date'
import BoyStatusFormModal from './BoyStatusFormModal'

// BOY status -> /titles-preview title id. Rider statuses map 1:1 onto their
// namesake title; horse "Rookie"/"Maiden" have no boy_status-driven title
// (Rookie Horse is granted via the horse's `rookies` flag, not this status).
const TITLE_ID_BY_BOY_STATUS = {
  rider: { Maiden: 8, Novice: 7, Open: 6 },
  horse: { Novice: 2, Open: 1 }
}

// ACA season rolls over at the start of February, matching SeasonSelect.vue
function seasonYear(dateString) {
  const date = new Date(dateString)
  return date.getUTCMonth() < 1 ? date.getUTCFullYear() - 1 : date.getUTCFullYear()
}

function buildColumns(type) {
  const columns = [
    { key: 'effective_date', label: 'Effective Date', sortable: true },
    { key: 'boy', label: 'BOY Status' },
    { key: 'status', label: type === 'horse' ? 'Horse Status' : 'Rider Status' }
  ]
  if (type === 'horse') columns.push({ key: 'owner', label: 'Owner' })
  if (type === 'rider') columns.push({ key: 'zone', label: 'Zone' })
  columns.push({ key: 'changed_by', label: 'Changed By' }, { key: 'actions', label: 'Actions', width: '150px' })
  return columns
}

// Below 560px: collapsed to an accordion. The summary line (Effective Date + BOY status)
// is always visible; the detail list repeats them alongside Status/Owner/Changed By/Actions
function buildBreakpoints(type) {
  const rows = [['effective_date'], ['boy'], ['status']]
  if (type === 'horse') rows.push(['owner'])
  if (type === 'rider') rows.push(['zone'])
  rows.push(['changed_by'], ['actions'])

  return [
    {
      maxWidth: 560,
      hideHeader: true,
      accordion: {
        summary: [{ keys: ['effective_date'], hideLabel: true }, { keys: ['boy'], hideLabel: true }]
      },
      rows
    }
  ]
}

export default defineComponent({
  name: 'BoyStatusHistoryTable',
  components: {
    ActionGateModal, AppButton, AppAlert, ConfirmModal, ResponsiveTable, BoyStatusFormModal
  },
  props: {
    entityId: {
      type: [String, Number],
      default: null
    },
    type: {
      type: String,
      required: true,
      validator: value => ['rider', 'horse'].includes(value)
    }
  },
  setup(props) {
    const { proxy } = getCurrentInstance()
    const { entityId } = toRefs(props)
    const columns = computed(() => buildColumns(props.type))
    const breakpoints = computed(() => buildBreakpoints(props.type))
    const {
      history, loading, statusOptions, ownerOptions, ownerLoading, saving, fetch, fetchStatusOptions, searchOwnerOptions, seedOwnerOptions, save, clearRecalculationFlag, remove
    } = useBoyStatusHistory(props.type)
    const actionGate = useActionGate()

    const formVisible = ref(false)
    const formError = ref(null)
    const editingRow = ref(null)
    const clearingFlagRow = ref(null)
    const deletingRow = ref(null)

    const sortField = ref(null)
    const sortDir = ref(null)

    function setSort(field) {
      if (sortField.value !== field) {
        sortField.value = field
        sortDir.value = 'asc'
      } else if (sortDir.value === 'asc') {
        sortDir.value = 'desc'
      } else {
        sortField.value = null
        sortDir.value = null
      }
    }

    const sortedHistory = computed(() => {
      if (!sortField.value) return history.value
      const dir = sortDir.value === 'asc' ? 1 : -1
      return [...history.value].sort(
        (a, b) => (new Date(a[sortField.value]) - new Date(b[sortField.value])) * dir
      )
    })

    function load(id) {
      actionGate.runGated(() => fetch(id)).catch(() => {})
    }

    onMounted(() => {
      fetchStatusOptions()
      if (entityId.value != null) load(entityId.value)
    })

    watch(entityId, (id, oldId) => {
      if (id != null && id !== oldId) load(id)
    })

    function openAdd() {
      editingRow.value = null
      formError.value = null
      formVisible.value = true
    }

    function openEdit(row) {
      editingRow.value = row
      formError.value = null
      seedOwnerOptions(row.owner)
      formVisible.value = true
    }

    function onOwnerSearch(query) {
      actionGate.runGated(() => searchOwnerOptions(entityId.value, query)).catch(() => {})
    }

    function viewResults(row) {
      proxy.$router.push({
        name: props.type === 'horse' ? 'Horse BOY Affected Results' : 'Member BOY Affected Results',
        params: { entityId: props.entityId, stateId: row.id }
      })
    }

    function boyStatusTitleLink(row) {
      const map = props.type === 'horse' ? TITLE_ID_BY_BOY_STATUS.horse : TITLE_ID_BY_BOY_STATUS.rider
      const titleId = map[row.boy_status.name]
      if (!titleId) return null
      return { path: '/titles-preview', query: { title: String(titleId), year: String(seasonYear(row.effective_date)) } }
    }

    function closeForm() {
      formVisible.value = false
      editingRow.value = null
      formError.value = null
    }

    function extractError(err) {
      const data = err.response != null ? err.response.data : null
      return data != null && data.message != null
        ? data.message
        : 'A server error occurred. Please try again later.'
    }

    async function handleSubmit(payload) {
      formError.value = null
      const stateId = editingRow.value ? editingRow.value.id : null
      try {
        await actionGate.runGated(() => save(entityId.value, stateId, payload))
        closeForm()
        await fetch(entityId.value)
      } catch (err) {
        if (err.isGateCancelled) return
        formError.value = extractError(err)
      }
    }

    function confirmClearFlag(row) {
      clearingFlagRow.value = row
    }

    async function handleClearFlag() {
      const row = clearingFlagRow.value
      try {
        await actionGate.runGated(() => clearRecalculationFlag(entityId.value, row.id))
        clearingFlagRow.value = null
        await fetch(entityId.value)
      } catch (err) {
        // errors surface via the global http-error alert (see http.js); gate cancellation is a no-op
      }
    }

    function confirmDelete(row) {
      deletingRow.value = row
    }

    async function handleDelete() {
      const row = deletingRow.value
      try {
        await actionGate.runGated(() => remove(entityId.value, row.id))
        deletingRow.value = null
        await fetch(entityId.value)
      } catch (err) {
        // errors surface via the global http-error alert (see http.js); gate cancellation is a no-op
      }
    }

    function rowClass(row) {
      return { 'rtable__record--warning': row.needs_recalculation }
    }

    return {
      history,
      sortedHistory,
      sortField,
      sortDir,
      setSort,
      loading,
      statusOptions,
      ownerOptions,
      ownerLoading,
      saving,
      formVisible,
      formError,
      editingRow,
      clearingFlagRow,
      deletingRow,
      formatDate,
      openAdd,
      openEdit,
      onOwnerSearch,
      viewResults,
      boyStatusTitleLink,
      closeForm,
      handleSubmit,
      confirmClearFlag,
      handleClearFlag,
      confirmDelete,
      handleDelete,
      rowClass,
      columns,
      breakpoints,
      ...actionGate
    }
  }
})
</script>

<style scoped>
.boy-history__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

@media (max-width: 500px) {
  .boy-history__header {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }
}

.draws-dashboard__warn-icon {
  display: inline-block;
  background-image: url('~@/assets/img/sprite.png');
  background-size: 99px 97px;
  background-position: -22px 0px;
  width: 21px;
  height: 18px;
  vertical-align: middle;
  margin-left: 0.4rem;
}
</style>



// WEBPACK FOOTER //
// src/components/BoyStatus/BoyStatusHistoryTable.vue